<template>
    <WorkloadRequirementsRow
        v-for="(requirementsForFunctionPerDayPerQuarter, functionId) in requirementsPerFunctionPerDayPerQuarter['working']"
        :key="functionId"
        :requirements="requirementsForFunctionPerDayPerQuarter"
        :functionName="resourceFunctionNames[functionId]"
        :fulfillment="fulfillmentPerFunctionPerDayPerQuarter[functionId] ?? {}"
        :allDays="roster.days"
        :precision="PRECISION"
    />
    <WorkloadRequirementsRow
        v-for="(requirementsForFunctionPerDayPerQuarterForReachability, functionId) in requirementsPerFunctionPerDayPerQuarter['reachable']"
        isReachable
        :key="functionId"
        :requirements="requirementsForFunctionPerDayPerQuarterForReachability"
        :functionName="resourceFunctionNames[functionId]"
        :fulfillment="fulfillmentPerFunctionPerDayPerQuarterForReachability[functionId] ?? {}"
        :allDays="roster.days"
        :precision="PRECISION"
    />
</template>

<script lang="ts">
    import WorkloadRequirementsRow from '@/components/Planbord/WorkloadRequirements/WorkloadRequirementsRow.vue';
    import {ApiClient, OrganisationDeploymentState, WorkloadRequirement, DeploymentType} from '@cquential/api';
    import {RosterData} from '@/types/types';
    import {PlanbordResource} from '@/types/api-object-extensions';
    import Dates from '@cquential/date-utils';
    import WorkingResourceMapCreator from '@/util/WorkloadRequirements/WorkingResourceMapCreator';
    import WorkloadFulfilmentCalculator from '@/util/WorkloadRequirements/WorkloadFulfilmentCalculator';
    import {Component, Vue, Prop, Watch} from 'vue-facing-decorator';
    import {ViewType} from '@/types/workloadRequirements';
    import GlobalErrorModal from '@/components/modals/GlobalErrorModal.vue';

    @Component({
        components: {
            WorkloadRequirementsRow,
        },
    })
    export default class WorkloadRequirementsBody extends Vue {
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({required: true}) public organisationDeploymentTypes!: Record<number, DeploymentType>;
        @Prop({required: true}) public resourceFunctionNames!: Record<number, string>;

        private readonly PRECISION: number = 900000; // 15 minutes, when changing, also rename some methods/variables (search for "quarter")

        private workloadRequirements: WorkloadRequirement[] = [];
        private loadedRosterId: number | null = null;
        private loadedBeginDate: Date | null = null;
        private loadedEndDate: Date | null = null;

        public get requirementsPerFunctionPerDayPerQuarter(): Record<ViewType, Record<number, Record<string, Record<number, WorkloadRequirement>>>> {
            const result: Record<ViewType, Record<number, Record<string, Record<number, WorkloadRequirement>>>> = {working: {}, reachable: {}};

            let type: ViewType;
            for (type in this.requirementsPerDayPerQuarterPerFunction) {
                for (const day in this.requirementsPerDayPerQuarterPerFunction[type]) {
                    for (const time in this.requirementsPerDayPerQuarterPerFunction[type][day]) {
                        const timeNumber: number = Number(time);
                        for (const functionId in this.requirementsPerDayPerQuarterPerFunction[type][day][timeNumber]) {
                            const functionNumber: number = Number(functionId);

                            result[type][functionNumber] ??= {};
                            result[type][functionNumber][day] ??= {};

                            result[type][functionNumber][day][timeNumber] = this.requirementsPerDayPerQuarterPerFunction[type][day][timeNumber][functionNumber];
                        }
                    }
                }
            }

            return result;
        }

        public get requirementsPerDayPerQuarterPerFunction(): Record<ViewType, Record<string, Record<number, Record<number, WorkloadRequirement>>>> {
            const result: Record<ViewType, Record<string, Record<number, Record<number, WorkloadRequirement>>>> = {working: {}, reachable: {}};

            for (const workloadRequirement of this.workloadRequirements) {
                const type: ViewType = workloadRequirement.isReachable ? 'reachable' : 'working';
                const day: string = Dates.Formatter.formatDateComputerFriendly(workloadRequirement.datetime);
                const time: number = workloadRequirement.datetime.getTime();
                const functionId: number = workloadRequirement.functionId ?? 0;
                result[type][day] ??= {};
                result[type][day][time] ??= {};
                if (functionId in result[type][day][time]) {
                    throw new Error(`Duplicate requirement found on ${day}/${time} for function ${functionId}.`);
                }

                result[type][day][time][functionId] = workloadRequirement;
            }

            return result;
        }

        public get fulfillmentPerFunctionPerDayPerQuarter(): Record<number, Record<string, Record<number, number>>> {
            const fulfilmentCalculator: WorkloadFulfilmentCalculator = new WorkloadFulfilmentCalculator(this.requirementsPerDayPerQuarterPerFunction['working'], this.resourcesWorkingPerDayPerQuarter);

            return fulfilmentCalculator.getResult();
        }

        public get fulfillmentPerFunctionPerDayPerQuarterForReachability(): Record<number, Record<string, Record<number, number>>> {
            const fulfilmentCalculator: WorkloadFulfilmentCalculator = new WorkloadFulfilmentCalculator(this.requirementsPerDayPerQuarterPerFunction['reachable'], this.resourcesWorkingPerDayPerQuarterForReachability);

            return fulfilmentCalculator.getResult();
        }

        public get resourcesWorkingPerDayPerQuarter(): Record<string, Record<number, PlanbordResource[]>> {
            const creator: WorkingResourceMapCreator = new WorkingResourceMapCreator(this.roster.rosterResources, this.PRECISION, this.organisationDeploymentStates, this.types['working']);

            return creator.getResult();
        }

        public get resourcesWorkingPerDayPerQuarterForReachability(): Record<string, Record<number, PlanbordResource[]>> {
            const creator: WorkingResourceMapCreator = new WorkingResourceMapCreator(this.roster.rosterResources, this.PRECISION, this.organisationDeploymentStates, this.types['reachable']);

            return creator.getResult();
        }

        public get types(): Record<ViewType, Record<number, true>> {
            const result: Record<ViewType, Record<number, true>> = {working: {}, reachable: {}};

            for (const typeId in this.organisationDeploymentTypes) {
                if (this.organisationDeploymentTypes[typeId].properties.reachable) {
                    result['reachable'][typeId] = true;
                } else if (this.organisationDeploymentTypes[typeId].properties.workingOrPresent) {
                    result['working'][typeId] = true;
                }
            }

            return result;
        }

        @Watch('roster', {deep: true})
        public onRosterChanged(): void {
            this.updateRequirements();
        }

        public mounted(): void {
            this.updateRequirements();
        }

        public updateRequirements(): void {
            if (this.roster.rosterId !== this.loadedRosterId
                || this.loadedBeginDate === null
                || this.roster.days[0].date.getTime() !== this.loadedBeginDate.getTime()
                || this.loadedEndDate === null
                || this.roster.days[this.roster.days.length - 1].date.getTime() !== this.loadedEndDate.getTime()
            ) {
                const rosterId: number = this.loadedRosterId = this.roster.rosterId;
                const beginDate: Date = this.loadedBeginDate = this.roster.days[0].date;
                const endDate: Date = this.loadedEndDate = this.roster.days[this.roster.days.length - 1].date;
                this.workloadRequirements = [];
                ApiClient.instance.fetchWorkloadRequirements(this.roster.rosterId, this.roster.days[0].date, this.roster.days[this.roster.days.length - 1].date)
                    .then((result: WorkloadRequirement[]): void => {
                        // Compare the updateRequirements-scoped values with the component-scoped values.
                        // They should be the same, if they were set by this updateRequirements call
                        if (rosterId === this.loadedRosterId && beginDate.getTime() === this.roster.days[0].date.getTime() && endDate.getTime() === this.roster.days[this.roster.days.length - 1].date.getTime()) {
                            this.workloadRequirements = result;
                        }
                    })
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
            }
        }
    }
</script>
